import React from "react";
import { useThemeEffect } from "@/hooks/use-theme";
import { EnhancedHeader } from "@/components/ui";
import { Separator } from "@/components/ui/separator";
import { ExternalLink, Heart, PackageOpen, Github, Code } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

interface PackageInfo {
  name: string;
  description: string;
  link?: string;
}

export default function OpenSourceCredits() {
  // Apply theme when component mounts
  useThemeEffect();

  // Package categories
  const uiPackages: PackageInfo[] = [
    {
      name: "React",
      description: "A JavaScript library for building user interfaces",
      link: "https://reactjs.org/",
    },
    {
      name: "Radix UI",
      description: "Unstyled, accessible components for building high‑quality design systems",
      link: "https://www.radix-ui.com/",
    },
    {
      name: "Tailwind CSS",
      description: "A utility-first CSS framework for rapid UI development",
      link: "https://tailwindcss.com/",
    },
    {
      name: "Framer Motion",
      description: "Production-ready animation library for React",
      link: "https://www.framer.com/motion/",
    },
    {
      name: "Lucide React",
      description: "Beautiful & consistent icons for React applications",
      link: "https://lucide.dev/",
    },
    {
      name: "class-variance-authority",
      description: "Type-safe UI components with variants support",
      link: "https://cva.style/docs",
    },
    {
      name: "React Day Picker",
      description: "Date picker component for React",
      link: "https://react-day-picker.js.org/",
    },
    {
      name: "Recharts",
      description: "A composable charting library built on React components",
      link: "https://recharts.org/",
    },
    {
      name: "React Resizable Panels",
      description: "Resizable panels for React applications",
      link: "https://github.com/bvaughn/react-resizable-panels",
    },
  ];

  const mobilePackages: PackageInfo[] = [
    {
      name: "Capacitor",
      description: "Cross-platform native runtime for web apps",
      link: "https://capacitorjs.com/",
    },
    {
      name: "Capacitor Core",
      description: "The core functionality of Capacitor",
      link: "https://capacitorjs.com/docs/apis",
    },
    {
      name: "Capacitor Android",
      description: "Android platform implementation for Capacitor",
      link: "https://capacitorjs.com/docs/android",
    },
    {
      name: "Capacitor iOS",
      description: "iOS platform implementation for Capacitor",
      link: "https://capacitorjs.com/docs/ios",
    },
    {
      name: "Capacitor Push Notifications",
      description: "Push notifications plugin for Capacitor",
      link: "https://capacitorjs.com/docs/apis/push-notifications",
    },
    {
      name: "Capacitor Splash Screen",
      description: "Splash screen plugin for Capacitor",
      link: "https://capacitorjs.com/docs/apis/splash-screen",
    },
  ];

  const authPackages: PackageInfo[] = [
    {
      name: "Firebase",
      description: "Google's platform for app development and backend services",
      link: "https://firebase.google.com/",
    },
    {
      name: "Firebase Auth",
      description: "User authentication service for Firebase",
      link: "https://firebase.google.com/docs/auth",
    },
    {
      name: "JSON Web Token",
      description: "Implementation of JSON Web Tokens",
      link: "https://github.com/auth0/node-jsonwebtoken",
    },
    {
      name: "bcryptjs",
      description: "Library for hashing passwords",
      link: "https://github.com/dcodeIO/bcrypt.js",
    },
    {
      name: "Passport",
      description: "Authentication middleware for Node.js",
      link: "http://www.passportjs.org/",
    },
  ];

  const dataPackages: PackageInfo[] = [
    {
      name: "Tanstack Query",
      description: "Powerful data synchronization for React",
      link: "https://tanstack.com/query/latest",
    },
    {
      name: "Drizzle ORM",
      description: "TypeScript ORM for SQL databases",
      link: "https://orm.drizzle.team/",
    },
    {
      name: "Zod",
      description: "TypeScript-first schema validation with static type inference",
      link: "https://zod.dev/",
    },
    {
      name: "Zustand",
      description: "A small, fast and scalable state-management solution",
      link: "https://github.com/pmndrs/zustand",
    },
    {
      name: "React Hook Form",
      description: "Performant, flexible form validation for React",
      link: "https://react-hook-form.com/",
    },
  ];

  const utilityPackages: PackageInfo[] = [
    {
      name: "Express",
      description: "Fast, unopinionated, minimalist web framework for Node.js",
      link: "https://expressjs.com/",
    },
    {
      name: "AWS SDK",
      description: "AWS SDK for JavaScript",
      link: "https://aws.amazon.com/sdk-for-javascript/",
    },
    {
      name: "date-fns",
      description: "Modern JavaScript date utility library",
      link: "https://date-fns.org/",
    },
    {
      name: "dayjs",
      description: "Fast 2kB alternative to Moment.js with the same API",
      link: "https://day.js.org/",
    },
    {
      name: "dotenv",
      description: "Loads environment variables from .env file",
      link: "https://github.com/motdotla/dotenv",
    },
    {
      name: "ws",
      description: "WebSocket client and server implementation",
      link: "https://github.com/websockets/ws",
    },
    {
      name: "nodemailer",
      description: "Send emails from Node.js",
      link: "https://nodemailer.com/",
    },
  ];

  return (
    <div>
      {/* Mobile-friendly navigation header */}
      <EnhancedHeader
        title="Open Source Credits"
        backPath="/user-settings"
        coloredText={true}
        rightContent={null}
        showBackButton={true}
      />
      <div className="min-h-screen themed-gradient text-white p-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="theme-card p-6">
            <div className="mb-6 text-center">
              <Badge className="mb-4 bg-pink-600/20 text-pink-300 border border-pink-500/40 px-3 py-1 text-sm">
                <Heart className="h-4 w-4 mr-2" /> Open Source
              </Badge>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-primary mb-2">
                Third-Party Acknowledgments
              </h1>
              <p className="text-gray-400">
                Spar Legends is built upon the incredible work of many open-source projects. We extend our gratitude to all the contributors who make these projects possible.
              </p>
            </div>

            <Separator className="my-6 bg-white/10" />

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <PackageOpen className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold">Packages & Libraries</h2>
              </div>
              <p className="text-gray-300">
                The following open source packages are used in the development of Spar Legends. Each package is governed by its own license terms.
              </p>
            </div>

            <Accordion type="multiple" className="space-y-4">
              <AccordionItem value="ui-libraries" className="border-white/10 rounded-lg bg-black/20 px-4">
                <AccordionTrigger className="text-white hover:no-underline">
                  UI & Component Libraries
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    {uiPackages.map((pkg) => (
                      <div key={pkg.name} className="bg-black/30 p-4 rounded-lg border border-white/10">
                        <div className="flex justify-between items-start">
                          <h3 className="font-medium text-primary">{pkg.name}</h3>
                          {pkg.link && (
                            <a
                              href={pkg.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 flex items-center"
                            >
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="mobile" className="border-white/10 rounded-lg bg-black/20 px-4">
                <AccordionTrigger className="text-white hover:no-underline">
                  Mobile & Capacitor Plugins
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    {mobilePackages.map((pkg) => (
                      <div key={pkg.name} className="bg-black/30 p-4 rounded-lg border border-white/10">
                        <div className="flex justify-between items-start">
                          <h3 className="font-medium text-primary">{pkg.name}</h3>
                          {pkg.link && (
                            <a
                              href={pkg.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 flex items-center"
                            >
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="auth" className="border-white/10 rounded-lg bg-black/20 px-4">
                <AccordionTrigger className="text-white hover:no-underline">
                  Authentication & Security
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    {authPackages.map((pkg) => (
                      <div key={pkg.name} className="bg-black/30 p-4 rounded-lg border border-white/10">
                        <div className="flex justify-between items-start">
                          <h3 className="font-medium text-primary">{pkg.name}</h3>
                          {pkg.link && (
                            <a
                              href={pkg.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 flex items-center"
                            >
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="data" className="border-white/10 rounded-lg bg-black/20 px-4">
                <AccordionTrigger className="text-white hover:no-underline">
                  Data Management
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    {dataPackages.map((pkg) => (
                      <div key={pkg.name} className="bg-black/30 p-4 rounded-lg border border-white/10">
                        <div className="flex justify-between items-start">
                          <h3 className="font-medium text-primary">{pkg.name}</h3>
                          {pkg.link && (
                            <a
                              href={pkg.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 flex items-center"
                            >
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="utilities" className="border-white/10 rounded-lg bg-black/20 px-4">
                <AccordionTrigger className="text-white hover:no-underline">
                  Utilities & Core Services
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    {utilityPackages.map((pkg) => (
                      <div key={pkg.name} className="bg-black/30 p-4 rounded-lg border border-white/10">
                        <div className="flex justify-between items-start">
                          <h3 className="font-medium text-primary">{pkg.name}</h3>
                          {pkg.link && (
                            <a
                              href={pkg.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 flex items-center"
                            >
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">{pkg.description}</p>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Separator className="my-6 bg-white/10" />

            <div className="text-center">
              
              <div className="text-gray-400 text-sm mt-4">
                <p>We're grateful to the open source community for making Spar Legends possible.</p>
                <p className="mt-2">© 2025 Spar Legends. All rights reserved.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}